import {COMPANY_KEYS} from "@/api/companyApi";
import {USER_KEYS} from "@/api/userApi";
import CaretIcon from "@/assets/colorable/caret.svg?react";
import BellIcon from "@/assets/light_gradient/bell.svg?react";
import NotificationCountIndicator from "@/components/NavBar/NotificationBadge";
import NotificationPanel from "@/components/NavBar/NotificationPanel";
import {useGenericQuery} from "@/hooks/useQueryHooks";
import {useSelectedCompanyId, useUserId} from "@/hooks/useUserHooks";
import clsxm from "@/lib/clsxm";
import useNotificationStore from "@/store/notification";
import type {TNotificationCounts} from "@/store/types";
import {AnimatePresence, motion} from "framer-motion";
import {useEffect, useRef, type FC} from "react";
import {useClickAway, useDebounce, useHoverDirty} from "react-use";

const Notifications: FC<{
  className?: string;
}> = ({className}) => {
  const {isOpen, toggleOpen, setValue} = useNotificationStore();
  const userId = useUserId();
  const selectedCompanyId = useSelectedCompanyId();
  const {data: userNotificationCounts} = useGenericQuery<TNotificationCounts>({
    queryKey: USER_KEYS.qUserNotificationCounts(userId),
    enabled: !!userId,
  });
  const {data: companyNotificationCounts} = useGenericQuery<TNotificationCounts>({
    queryKey: COMPANY_KEYS.qCompanyNotificationCounts(selectedCompanyId!),
    enabled: !!selectedCompanyId,
  });

  const ref = useRef<HTMLDivElement>(null!);
  const isHovering = useHoverDirty(ref);
  useDebounce(() => toggleOpen(isHovering), isHovering ? 300 : 200, [isHovering]);

  useEffect(() => {
    if (companyNotificationCounts && userNotificationCounts) {
      setValue("notificationCounts", {
        notification: userNotificationCounts.notification,
        chat: companyNotificationCounts.chat,
      });
    }
  }, [companyNotificationCounts, userNotificationCounts, setValue]);

  const notificationCount = useNotificationStore(state => state.notificationCounts.notification.unseen);

  useClickAway(ref, () => {
    if (isOpen) return toggleOpen(false);
  });

  return (
    <div className={className} ref={ref}>
      <div className="group flex items-center justify-center">
        <div className="cursor-pointer rounded-full transition-transform duration-150 group-hover:scale-110">
          <div
            className={`border-primary-light-solid relative flex size-10 items-center justify-center rounded-sm border ${isOpen ? "bg-neutral-white-dark" : ""}`}>
            <BellIcon
              className={clsxm(
                "group-hover:text-primary-dark-solid size-[18px] transition-all duration-200",
                isOpen ? "text-primary-dark-solid" : "text-neutral-grey-light",
              )}
            />
            <NotificationCountIndicator
              isFilled
              count={notificationCount}
              className="border-neutral-white-dark absolute top-0.5 right-[3px] origin-top-right border-2"
            />
          </div>
        </div>
      </div>
      <AnimatePresence>
        {isOpen && (
          <motion.div initial={{opacity: 0}} animate={{opacity: 1}} exit={{opacity: 0}} transition={{duration: 0.2}}>
            <div className="absolute -bottom-1">
              <CaretIcon className="text-neutral-grey-light-dark h-5 w-10" />
            </div>
            <NotificationPanel />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Notifications;
